import React, { ReactNode } from 'react';
import { styled, Theme } from '@mui/material/styles';
import MuiAvatar, { AvatarProps as MuiAvatarProps } from '@mui/material/Avatar';

import getColors from 'utils/getColors';

type AvatarColor = 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
type AvatarType = 'filled' | 'outlined' | 'combined';
type AvatarSize = 'badge' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

function getColorStyle({ theme, color, type }: { theme: Theme; color?: string; type?: AvatarType }) {
  const colors = getColors(theme, color);
  const { lighter, light, main, dark, contrastText } = colors;

  // No dark o `main` das famílias semânticas é um tom fundo e some sobre o tint escuro do
  // `lighter` — lá a cor legível é o `dark` (nesses ramps ele é o tom mais claro).
  const acento = `light-dark(${main}, ${dark})`;

  switch (type) {
    case 'filled':
      return { color: contrastText, background: main };
    case 'outlined':
      return { color: acento, border: '1px solid', borderColor: acento, background: 'transparent' };
    case 'combined':
      return { color: acento, border: '1px solid', borderColor: light, background: lighter };
    default:
      return { color: acento, background: lighter };
  }
}

function getSizeStyle(size?: AvatarSize) {
  switch (size) {
    case 'badge':
      return { border: '2px solid', fontSize: '0.675rem', width: 20, height: 20 };
    case 'xs':
      return { fontSize: '0.75rem', width: 24, height: 24 };
    case 'sm':
      return { fontSize: '0.875rem', width: 32, height: 32 };
    case 'lg':
      return { fontSize: '1.2rem', width: 52, height: 52 };
    case 'xl':
      return { fontSize: '1.5rem', width: 64, height: 64 };
    case 'md':
    default:
      return { fontSize: '1rem', width: 40, height: 40 };
  }
}

interface AvatarStyleProps {
  size?: AvatarSize;
  type?: AvatarType;
}

const AvatarStyle = styled(MuiAvatar, {
  shouldForwardProp: (prop) => prop !== 'color' && prop !== 'type' && prop !== 'size'
})<AvatarStyleProps & { color?: string }>(({ theme, size, color, type }) => ({
  ...getSizeStyle(size),
  ...getColorStyle({ theme, color, type }),
  variants: [
    {
      props: { size: 'badge' },
      style: { borderColor: theme.vars!.palette.background.default }
    }
  ]
}));

interface AvatarProps extends Omit<MuiAvatarProps, 'color'> {
  children?: ReactNode;
  color?: AvatarColor;
  type?: AvatarType;
  size?: AvatarSize;
}

export default function Avatar({ children, color = 'primary', type, size = 'md', ...others }: AvatarProps) {
  const Styled = AvatarStyle as unknown as React.ComponentType<
    Omit<MuiAvatarProps, 'color'> & AvatarStyleProps & { color?: string }
  >;
  return (
    <Styled color={color} type={type} size={size} {...(others as Omit<MuiAvatarProps, 'color'>)}>
      {children}
    </Styled>
  );
}
